import React, { useState, useEffect } from 'react';
import {
  INVESTIGATION_TEMPLATES,
  CULTURE_MEDIA_OPTIONS
} from '../utils/microbiologyTemplateEngine';

// Specimen type decides which media template applies (same logic the rest of
// this file already used for colony-count presets etc.) — used both to pick
// the standard SOP plate panel for a brand-new culture and to react to the
// specimen shown.
const resolveTemplate = (specimenName) => {
  const name = (specimenName || '').toLowerCase();
  if (name.includes('blood')) return INVESTIGATION_TEMPLATES.BLOOD;
  if (name.includes('sputum') || name.includes('bal')) return INVESTIGATION_TEMPLATES.SPUTUM;
  if (name.includes('pus') || name.includes('wound') || name.includes('swab')) return INVESTIGATION_TEMPLATES.PUS_SWAB;
  if (name.includes('stool')) return INVESTIGATION_TEMPLATES.STOOL;
  if (name.includes('fluid') || name.includes('csf')) return INVESTIGATION_TEMPLATES.BODY_FLUID;
  return INVESTIGATION_TEMPLATES.URINE;
};

// A brand-new culture (nothing saved yet) starts from the lab's standard SOP
// media panel for that specimen type, with empty readings — matching how a
// real lab always plates the same media set for a given specimen. Once the
// technician saves, the real entered readings come back from the server on
// the next load instead of this template.
const buildStandardPanel = (template) =>
  (template.defaultMedia || []).map((mediumName, idx) => ({
    id: `new-${idx}`,
    mediaID: null,
    mediumName,
    temperature: '35°C–37°C',
    atmosphere: 'Ambient Air',
    day1Reading: '',
    day2Reading: '',
    plateStatus: 'Pending'
  }));

const mapSavedPlates = (plates) =>
  plates.map((m) => ({
    id: m.mediaID,
    mediaID: m.mediaID,
    mediumName: m.mediumName,
    temperature: m.temperature || '35°C–37°C',
    atmosphere: m.atmosphere || 'Ambient Air',
    day1Reading: m.day1Reading || '',
    day2Reading: m.day2Reading || '',
    plateStatus: m.plateStatus || 'Pending'
  }));

export default function CultureSection({
  data,
  onSaveCultureStatus,
  onProceedToOrganisms
}) {
  const [cultureStatus, setCultureStatus] = useState(data?.cultureStatus || 'Pending');
  const [colonyCount, setColonyCount] = useState(data?.colonyCount || '');
  const [growthSignificance, setGrowthSignificance] = useState(
    data?.growthSignificance || 'Significant Growth'
  );

  const currentTemplate = resolveTemplate(data?.specimenName);

  // Multi-medium plate tracking: real saved plates if this culture already
  // has them, otherwise the specimen's standard SOP panel (empty readings).
  const [mediaTrackingList, setMediaTrackingList] = useState(() =>
    data?.mediaPlates && data.mediaPlates.length > 0
      ? mapSavedPlates(data.mediaPlates)
      : buildStandardPanel(currentTemplate)
  );

  const [newMediumName, setNewMediumName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setCultureStatus(data.cultureStatus || 'Pending');
      setColonyCount(data.colonyCount || '');
      setGrowthSignificance(data.growthSignificance || 'Significant Growth');
      setMediaTrackingList(
        data.mediaPlates && data.mediaPlates.length > 0
          ? mapSavedPlates(data.mediaPlates)
          : buildStandardPanel(resolveTemplate(data.specimenName))
      );
    }
  }, [data]);

  const handleStatusSelect = (status) => {
    setCultureStatus(status);
    if (status === 'No Growth') {
      setColonyCount('No colony growth detected after 48h incubation');
      setGrowthSignificance('Insignificant / Sterile');
    } else if (status === 'Growth') {
      if (!colonyCount || colonyCount.includes('No colony')) {
        setColonyCount('> 10^5 CFU/mL (Significant Bacteriuria)');
      }
      setGrowthSignificance('Significant Pure Growth (Primary Pathogen)');
    } else if (status === 'Mixed Growth') {
      setGrowthSignificance('Mixed Growth (Likely Contaminant / Commensal)');
    }
  };

  const handleAddMedium = () => {
    if (!newMediumName) return;
    const newEntry = {
      id: Date.now(),
      mediumName: newMediumName,
      inoculationTime: new Date().toISOString(),
      temperature: '35°C–37°C',
      atmosphere: 'Ambient Air',
      readingSchedule: '24h / Day 1',
      day1Reading: '',
      day2Reading: '',
      plateStatus: 'Pending'
    };
    setMediaTrackingList([...mediaTrackingList, newEntry]);
    setNewMediumName('');
  };

  const handleRemoveMedium = (id) => {
    setMediaTrackingList(mediaTrackingList.filter((m) => m.id !== id));
  };

  const handleUpdateMediumReading = (id, field, value) => {
    setMediaTrackingList(
      mediaTrackingList.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  };

  const handleSave = async (isProceed = false) => {
    setSaving(true);
    try {
      const mediaPayload = mediaTrackingList.map((m) => ({
        mediaID: typeof m.mediaID === 'number' ? m.mediaID : 0,
        mediumName: m.mediumName,
        temperature: m.temperature,
        atmosphere: m.atmosphere,
        day1Reading: m.day1Reading,
        day2Reading: m.day2Reading,
        plateStatus: m.plateStatus
      }));
      await onSaveCultureStatus(cultureStatus, colonyCount, growthSignificance, data?.isCriticalAlert, mediaPayload);
      if (isProceed && onProceedToOrganisms) {
        onProceedToOrganisms();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wb-stage-view">
      {/* Step Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">Culture Incubation & Plate Readings</h3>
          <span className="wb-stage-subtitle">
            Specimen: <strong>{data?.specimenName}</strong> &bull; Inoculation:{' '}
            <strong>{data?.inoculationDate ? new Date(data.inoculationDate).toLocaleDateString() : 'Active'}</strong> &bull; Elapsed:{' '}
            <strong>{data?.daysInIncubation || 1} day(s)</strong>
          </span>
        </div>
      </div>

      {/* 1. Primary Culture Status Segmented Bar */}
      <div className="wb-field-group" style={{ marginBottom: '16px' }}>
        <label className="wb-field-label">
          Overall Culture Status <span className="wb-req">*</span>
        </label>
        <div className="wb-segmented-control-lg">
          {[
            { id: 'Pending', label: '⏳ Pending Incubation' },
            { id: 'Growth', label: '🧫 Growth Detected' },
            { id: 'No Growth', label: '🛡️ No Growth (Sterile)' },
            { id: 'Mixed Growth', label: '⚠️ Mixed Growth / Commensal' },
            { id: 'Contaminated', label: '❌ Contaminated' }
          ].map((st) => (
            <button
              key={st.id}
              type="button"
              className={`wb-seg-btn-lg ${cultureStatus === st.id ? 'active' : ''}`}
              onClick={() => handleStatusSelect(st.id)}
            >
              {st.label}
            </button>
          ))}
        </div>
      </div>

      {/* 2. Inoculated Media & Plate Readings Table */}
      <div className="wb-card-clean">
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title">Inoculated Media & Plate Observations</h4>
            <span className="wb-hint-text">
              Track multi-media incubation conditions and Day 1 (24h) / Day 2 (48h) findings.
            </span>
          </div>

          {/* Add Plate Tool */}
          <div className="wb-header-tools">
            <select
              className="wb-select-compact"
              style={{ width: '220px' }}
              value={newMediumName}
              onChange={(e) => setNewMediumName(e.target.value)}
            >
              <option value="">+ Add Culture Medium...</option>
              {CULTURE_MEDIA_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="wb-btn-secondary-sm"
              onClick={handleAddMedium}
              disabled={!newMediumName}
            >
              + Inoculate Plate
            </button>
          </div>
        </div>

        <div className="wb-table-wrapper">
          <table className="wb-clinical-table">
            <thead>
              <tr>
                <th>Medium Name</th>
                <th style={{ width: '120px' }}>Environment</th>
                <th>Day 1 Reading (24h)</th>
                <th>Day 2 Reading (48h)</th>
                <th style={{ width: '150px' }}>Plate Finding</th>
                <th style={{ width: '50px', textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {mediaTrackingList.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.mediumName}</strong>
                  </td>
                  <td>
                    <span className="wb-tag-neutral">{m.temperature}</span>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="wb-input-cell"
                      value={m.day1Reading || ''}
                      onChange={(e) => handleUpdateMediumReading(m.id, 'day1Reading', e.target.value)}
                      placeholder="Day 1 colonial morphology..."
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="wb-input-cell"
                      value={m.day2Reading || ''}
                      onChange={(e) => handleUpdateMediumReading(m.id, 'day2Reading', e.target.value)}
                      placeholder="Day 2 colonial finding..."
                    />
                  </td>
                  <td>
                    <select
                      className="wb-select-cell"
                      value={m.plateStatus}
                      onChange={(e) => handleUpdateMediumReading(m.id, 'plateStatus', e.target.value)}
                    >
                      <option value="Growth Detected">Growth Detected</option>
                      <option value="No Growth">No Growth</option>
                      <option value="Pending">Pending</option>
                      <option value="Contaminated">Contaminated</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="wb-btn-remove-row"
                      onClick={() => handleRemoveMedium(m.id)}
                      title="Remove plate"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. Colony Count Quantification & Growth Significance */}
      <div className="wb-clinical-form-layout" style={{ marginTop: '16px' }}>
        <div className="wb-form-column">
          <div className="wb-field-group">
            <label className="wb-field-label">
              Colony Count Quantification ({currentTemplate.colonyCountUnit})
            </label>
            <div className="wb-input-with-select">
              <select
                className="wb-select"
                style={{ marginBottom: '6px' }}
                value={colonyCount}
                onChange={(e) => setColonyCount(e.target.value)}
              >
                <option value="">-- Select Standard Range --</option>
                {currentTemplate.colonyCountPresets?.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="wb-input"
                value={colonyCount}
                onChange={(e) => setColonyCount(e.target.value)}
                placeholder="Or enter numeric count (e.g. 150,000 CFU/mL)..."
              />
            </div>
          </div>
        </div>

        <div className="wb-form-column">
          <div className="wb-field-group">
            <label className="wb-field-label">Clinical Growth Significance</label>
            <select
              className="wb-select"
              value={growthSignificance}
              onChange={(e) => setGrowthSignificance(e.target.value)}
            >
              {[
                'Significant Pure Growth (Primary Pathogen)',
                'Equivocal Count / Correlate Clinically',
                'Commensal / Normal Resident Flora',
                'Superficial / Skin Contaminant',
                'Insignificant / Sterile'
              ].map((sig) => (
                <option key={sig} value={sig}>
                  {sig}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* In-Page Action Toolbar */}
      <div className="wb-stage-actions-bar">
        <div className="wb-actions-right">
          <button
            type="button"
            className="wb-btn-primary"
            disabled={saving}
            onClick={() => handleSave(false)}
          >
            {saving ? 'Saving...' : 'Save Culture Status'}
          </button>
        </div>
      </div>
    </div>
  );
}
